import React, { useCallback } from 'react';
import * as Clipboard from 'expo-clipboard';
import { NotificationFeedbackType } from 'expo-haptics';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Linking, StyleSheet, View } from 'react-native';

import {
  Container,
  theme,
  triggerHapticNotificationFeedback,
  useNoticeBannersStackContext,
  useResponsive,
  useShareTextDialog,
} from '@sg/garnish';

import {
  GroupOrderModalCloseButton,
  GroupOrderModalContent,
  GroupOrderModalFooter,
  GroupOrderModalScrollView,
  GroupOrderShare,
} from '@order/features-group-ordering';
import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderShareContent = (props: Props) => {
  const { joinCode, onClose, onDone } = props;

  const { push: addBanner } = useNoticeBannersStackContext();
  const { share } = useShareTextDialog();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { match } = useResponsive();

  // ─── Derived ──────────────────────────────────────────────────────────────

  const shareUrl = buildGroupOrderShareUrl(joinCode);
  const displayUrl = stripShareUrlProtocol(shareUrl);

  const shareMessage = formatMessage(messages.shareMessage, { share_url: shareUrl });
  const shareSubject = formatMessage(messages.shareSubject);
  const copySuccessText = formatMessage(messages.copyLinkSuccess);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleCopyLink = useCallback(async (): Promise<boolean> => {
    const hasCopied = await Clipboard.setStringAsync(shareUrl);

    if (!hasCopied) return false;

    track('group_ordering.share.copy_link_tapped');

    await triggerHapticNotificationFeedback(NotificationFeedbackType.Success);

    addBanner(
      { id: 'go-share-success', palette: 'success', text: copySuccessText, autoHideTimeout: 1800 },
      true,
    );

    return true;
  }, [addBanner, copySuccessText, shareUrl, track]);

  const handleShareEmail = useCallback(async () => {
    track('group_ordering.share.email_tapped');

    await Linking.openURL(
      `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareMessage)}`,
    );
  }, [shareMessage, shareSubject, track]);

  const handleShareMessage = useCallback(async () => {
    track('group_ordering.share.message_tapped');

    await Linking.openURL(`sms:?body=${encodeURIComponent(shareMessage)}`);
  }, [shareMessage, track]);

  const handleShareMore = useCallback(async () => {
    track('group_ordering.share.more_tapped');

    try {
      await share({ url: shareUrl, message: shareMessage });
    } catch {}
  }, [share, shareMessage, shareUrl, track]);

  // ─── Styles ───────────────────────────────────────────────────────────────

  const contentContainerStyles = [
    styles.contentContainer,
    match([undefined, styles.contentContainerSM]),
  ];

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <GroupOrderModalContent>
      <GroupOrderModalScrollView showsVerticalScrollIndicator={false} bounces={false}>
        <Container style={contentContainerStyles}>
          <View style={styles.headerContainer}>
            <GroupOrderModalCloseButton
              accessibilityLabel={formatMessage(messages.closeA11yLabel)}
              onPress={onClose}
            />

            <GroupOrderShare.Header />
          </View>

          <GroupOrderShare.LinkRow joinLink={displayUrl} onCopyLink={handleCopyLink} />

          <GroupOrderShare.ShareActions
            onShareEmail={handleShareEmail}
            onShareMessage={handleShareMessage}
            onShareMore={handleShareMore}
          />
        </Container>
      </GroupOrderModalScrollView>

      <GroupOrderModalFooter>
        <GroupOrderShare.DoneButton onPress={onDone} />
      </GroupOrderModalFooter>
    </GroupOrderModalContent>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildGroupOrderShareUrl(joinCode: string) {
  const appUrl = getEnvVars().APP_URL as string;

  return new URL(`/g/${joinCode}`, appUrl).toString();
}

function stripShareUrlProtocol(url: string) {
  const { host, pathname, search } = new URL(url);

  return `${host}${pathname}${search}`;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly closeA11yLabel: NoMessageValues;
  readonly copyLinkSuccess: NoMessageValues;
  readonly shareSubject: NoMessageValues;
  readonly shareMessage: { readonly share_url: MessageValue };
}>({
  closeA11yLabel: {
    defaultMessage: 'Close',
    description: 'Group Order Share | Modal | Close button a11y label',
  },
  copyLinkSuccess: {
    defaultMessage: 'Group order link copied',
    description: 'Group Order Share | Modal | Copy link success banner',
  },
  shareSubject: {
    defaultMessage: 'Join my sweetgreen group order',
    description: 'Group Order Share | Modal | Share email subject',
  },
  shareMessage: {
    defaultMessage: 'Join my sweetgreen group order: {share_url}',
    description: 'Group Order Share | Modal | Share message body',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerContainer: {
    gap: theme.spacing['6'],
  },
  contentContainer: {
    gap: theme.spacing['8'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  contentContainerSM: {
    paddingHorizontal: theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  joinCode: string;
  onClose: () => void;
  onDone: () => void;
};
